import type { SearchResultV5 } from '@seek/chalice-types';
import checksum from '@seek/request-checksum';
import axios, { type AxiosResponse, type CancelTokenSource } from 'axios';
import get from 'lodash/get';
import has from 'lodash/has';

import {
  searchUrlV5 as jobSearchUrlV5,
  personalisedSearchUrlV5 as personalisedJobSearchUrlV5,
} from 'src/config';
import {
  createAuthenticatedHttpClient,
  createUnauthenticatedHttpClient,
  withDevAuth,
  withLastKnownSolUserId,
  withServerHeaders,
} from 'src/modules/seek-api-request';
import type { CookieType } from 'src/types/cookie';

import { buildApiHeaders } from './utils/buildApiHeaders';
import { buildApiQuery } from './utils/buildApiQuery';
import { SEARCH_API_TIMEOUT } from './utils/constants';
import { getAuthClientDetails } from './utils/getAuthClientDetails';
import { getJobSearchDarkProdUrl } from './utils/getJobSearchDarkProdUrl';
import type { BaseAPIProps } from './utils/types';

let onlySearchPromise: Promise<SearchResultV5> | undefined,
  onlySearchPromiseSource: CancelTokenSource | undefined;

const getSearchUrl = ({
  isAuthenticated,
  cookies,
}: {
  isAuthenticated: boolean;
  cookies: CookieType;
}) => {
  const darkProdSearchUrlSearch = getJobSearchDarkProdUrl({
    cookies,
    path: '/search',
  });

  if (darkProdSearchUrlSearch) {
    return darkProdSearchUrlSearch;
  }

  if (isAuthenticated) {
    return personalisedJobSearchUrlV5;
  }
  return jobSearchUrlV5;
};

export const search = ({
  searchParams,
  brand,
  country,
  zone,
  cookies,
  userQueryId,
  requestId,
  timeout = SEARCH_API_TIMEOUT,
  userAgent,
  sessionId,
  userClientId,
  seekerId,
  solId,
  testHeaders,
  locale,
  serverProps,
  isAuthenticated,
  relatedSearchesCount,
  baseKeywords,
  source,
  newSince,
  isPreviousQueryModified,
  previousIntentSuggestions,
  shouldIncludeQueryHints,
}: BaseAPIProps & {
  userQueryId: string;
  userAgent: string;
  seekerId?: number;
  serverProps?: Record<string, string | undefined>;
  isAuthenticated: boolean;
  relatedSearchesCount?: number;
  baseKeywords?: string;
  source?: string;
  newSince?: number;
  isPreviousQueryModified?: boolean;
  previousIntentSuggestions?: SearchResultV5['intentSuggestions'];
  shouldIncludeQueryHints: boolean;
}) => {
  if (onlySearchPromise && onlySearchPromiseSource) {
    onlySearchPromiseSource.cancel();
  }

  const isSQCEnabled =
    (cookies['feature_experiment-search-query-correction'] !== 'control' &&
      typeof isPreviousQueryModified === 'undefined') ||
    (isPreviousQueryModified &&
      previousIntentSuggestions &&
      previousIntentSuggestions?.length > 0 &&
      previousIntentSuggestions[0].params?.keywords !== searchParams.keywords);

  const apiQuery = buildApiQuery({
    baseKeywords,
    searchQuery: searchParams,
    country,
    zone,
    userQueryId,
    seekerId,
    solId,
    locale,
    relatedSearchesCount,
    source,
    sessionId,
    userClientId,
    newSince,
    ...(isSQCEnabled && shouldIncludeQueryHints
      ? /*
         * TODO (Search Query Correction) - :
         * - For Scenario #1 & #2, queryHints param initially will only include 'spellingCorrection'.
         * - Further queryHints values, 'all,companySuggestion' are to be added.
         * - Reference: https://myseek.atlassian.net/browse/DJS-1530
         */
        { queryHints: 'spellingCorrection' }
      : {}),
  });

  const { isAuthenticatedClient, useDevAuth } =
    getAuthClientDetails(isAuthenticated);

  const config = {
    retryPolicy: { retries: 0 },
    defaultRequestConfig: {
      headers: {
        ...testHeaders,
        ...withLastKnownSolUserId(cookies['last-known-sol-user-id']),
        ...(useDevAuth ? withDevAuth(cookies.AUTH_TOKEN) : {}),
      },
    },
  };

  const httpClient = isAuthenticatedClient
    ? createAuthenticatedHttpClient(config)
    : createUnauthenticatedHttpClient(config);

  const searchPromise = httpClient
    .send({
      url: getSearchUrl({ isAuthenticated, cookies }),
      timeout,
      params: apiQuery,
      label: 'jobsearch-api-search',
      cancelToken: get(onlySearchPromiseSource, 'token'),
      headers: {
        ...buildApiHeaders(requestId),
        'x-seek-checksum': checksum(apiQuery),
        'seek-request-brand': brand,
        'seek-request-country': country,
        ...withServerHeaders({
          userAgent,
          ...serverProps,
        }),
      },
    })
    .then((response: AxiosResponse<SearchResultV5>) => {
      const result = get(response, 'data');

      if (has(result, 'data')) {
        return result;
      }

      throw new Error(`invalid api response: ${JSON.stringify(result)}`);
    });

  const isClient = ENV.CLIENT;
  if (isClient) {
    onlySearchPromise = searchPromise;
    onlySearchPromiseSource = axios.CancelToken.source();
  }

  return searchPromise;
};
